import React, { useEffect, useState } from "react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { Skeleton } from "../../components/ui/skeleton";
import api from "../../lib/api";
import { money } from "../../lib/statuses";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Clock, Truck, XCircle, CreditCard, Receipt, Wallet } from "lucide-react";

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/reports").then(({ data }) => setData(data)); }, []);

  if (!data) return <AdminLayout title="Reports"><div className="grid gap-4 lg:grid-cols-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}</div></AdminLayout>;

  const METRICS = [
    { label: "Avg Resolution Time", value: `${data.avg_resolution_days} days`, icon: Clock, color: "text-blue-600 bg-blue-50" },
    { label: "Avg Vendor Response", value: `${data.avg_vendor_response_days} days`, icon: Truck, color: "text-purple-600 bg-purple-50" },
    { label: "Rejected Claims", value: data.rejected_claims, icon: XCircle, color: "text-rose-600 bg-rose-50" },
    { label: "Credit Approved", value: money(data.credit_approved_value), icon: CreditCard, color: "text-emerald-600 bg-emerald-50" },
    { label: "CN Received", value: money(data.cn_received_value), icon: Receipt, color: "text-blue-600 bg-blue-50" },
    { label: "Available Supplier Credit", value: money(data.available_supplier_credit), icon: Wallet, color: "text-emerald-600 bg-emerald-50" },
    { label: "Credit Used", value: money(data.credit_used_value), icon: CreditCard, color: "text-slate-600 bg-slate-100" },
    { label: "CN Outstanding", value: money(data.cn_outstanding_value), icon: Receipt, color: "text-amber-600 bg-amber-50" },
  ];

  return (
    <AdminLayout title="Reports">
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {METRICS.map((m) => (
          <div key={m.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${m.color}`}><m.icon className="h-4 w-4" /></div>
            <div className="font-display text-lg font-bold text-slate-900">{m.value}</div>
            <div className="text-xs text-slate-500">{m.label}</div>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <ReportChart title="Claims by Supplier" data={data.by_supplier} color="#9333ea" vertical />
        <ReportChart title="Claims by Brand" data={data.by_brand} color="#2563EB" />
        <ReportChart title="Claims by Product" data={data.by_product} color="#10b981" vertical />
        <ReportChart title="Claims by Status" data={data.by_status} color="#f59e0b" vertical />
      </div>
    </AdminLayout>
  );
}

function ReportChart({ title, data, color, vertical }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-display text-base font-semibold text-slate-900">{title}</h3>
      <ResponsiveContainer width="100%" height={Math.max(260, data.length * 28)}>
        {vertical ? (
          <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
            <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
            <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <Tooltip /><Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} />
          </BarChart>
        ) : (
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" />
            <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" allowDecimals={false} />
            <Tooltip /><Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}
